import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/pages/fly-app/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { setTenantContext, clearTenantContext } from '@/lib/sentry';

interface Workspace {
  id: string;
  name: string;
  owner_email?: string;
  brand_name?: string;
  logo_url?: string;
  website?: string;
  notification_email?: string;
  notification_phone?: string;
  business_description?: string;
  location_naming_convention?: string;
  primary_category?: string;
  additional_categories?: string[];
  country?: string;
  timezone?: string;
  posting_footer?: string;
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  pinterest_url?: string;
  threads_url?: string;
  whatsapp_url?: string;
  is_default: boolean;
  settings?: any;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

interface WorkspaceSearchResult {
  workspaces: Workspace[];
  hasMore: boolean;
  total: number;
}

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  loading: boolean;
  currentUserRole: string | null;
  isWorkspaceOwner: boolean;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  setCurrentWorkspace: (workspace: Workspace) => void;
  createWorkspace: (data: {
    name: string;
    brand_name?: string;
    logo_url?: string;
    website?: string;
    notification_email?: string;
    notification_phone?: string;
    business_description?: string;
    country?: string;
    timezone?: string;
    agency_id?: string;
  }) => Promise<Workspace | null>;
  updateWorkspace: (workspaceId: string, data: Partial<Workspace>) => Promise<void>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  searchWorkspaces: (query: string, offset: number, limit: number) => Promise<WorkspaceSearchResult>;
  workspaceResetKey: number;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider = ({ children }: WorkspaceProviderProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [isWorkspaceOwner, setIsWorkspaceOwner] = useState(false);
  const [workspaceResetKey, setWorkspaceResetKey] = useState(0);

  // Track if this is initial load and previous user ID to prevent unnecessary reloads
  const isInitialLoad = useRef(true);
  const previousUserId = useRef<string | undefined>(undefined);

  // Load user's role for the current workspace
  const loadUserRole = async (workspaceId: string, userId: string, workspaceOwnerId: string) => {
    // Check if user is the owner
    if (userId === workspaceOwnerId) {
      setIsWorkspaceOwner(true);
      setCurrentUserRole('owner');
      return;
    }

    // Check if user is a super admin — grant full owner-level access
    try {
      const { data: opsTeamMember } = await supabase
        .from('ops_team_members')
        .select('role')
        .eq('email', user?.email?.toLowerCase())
        .eq('is_active', true)
        .eq('role', 'super_admin')
        .maybeSingle();
      if (opsTeamMember) {
        setIsWorkspaceOwner(true);
        setCurrentUserRole('owner');
        return;
      }
    } catch {
      // ops_team_members check failed, continue with normal role resolution
    }

    setIsWorkspaceOwner(false);

    try {
      // Check workspace_users for role_type (team invites use role_type column)
      const { data: workspaceUser, error } = await supabase
        .from('workspace_users')
        .select('role_type, invitation_status')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .eq('invitation_status', 'active')
        .single();

      if (!error && workspaceUser && workspaceUser.role_type) {
        // role_type values are already: 'admin', 'content_creator', 'client'
        setCurrentUserRole(workspaceUser.role_type);
        return;
      }

      // Check workspace_members as fallback (table may not exist)
      try {
        const { data: workspaceMember, error: memberError } = await supabase
          .from('workspace_members')
          .select('role')
          .eq('workspace_id', workspaceId)
          .eq('user_id', userId)
          .single();

        if (!memberError && workspaceMember) {
          // Map workspace_members role to role_type
          const memberRoleMap: Record<string, string> = {
            owner: 'owner',
            admin: 'admin',
            member: 'content_creator',
            viewer: 'client'
          };
          setCurrentUserRole(memberRoleMap[workspaceMember.role] || 'client');
          return;
        }
      } catch {
        // workspace_members table may not exist, continue
      }

      // No role found - might be accessing via managed_by
      setCurrentUserRole('admin'); // Default to admin for managed workspaces
    } catch (err) {
      console.error('[WorkspaceContext] Error loading user role:', err);
      setCurrentUserRole(null);
    }
  };

  // Wrapper to set workspace and load role
  const setCurrentWorkspace = (workspace: Workspace | null) => {
    setCurrentWorkspaceState(workspace);
    if (workspace && user) {
      loadUserRole(workspace.id, user.id, (workspace as any).owner_id);
      // Stamp tenant tags on every subsequent Sentry event so triage
      // can filter by workspace / agency without hopping to the DB.
      // Cleared on sign-out via clearTenantContext in the effect below.
      setTenantContext({
        workspace_id: workspace.id,
        agency_id: (workspace as any).agency_id ?? null,
        is_agency_domain:
          typeof window !== 'undefined' &&
          !!(window as any).__AGENCY_BRANDING__?.id,
        agency_slug:
          typeof window !== 'undefined'
            ? (window as any).__AGENCY_BRANDING__?.slug ?? null
            : null,
      });
    } else {
      setCurrentUserRole(null);
      setIsWorkspaceOwner(false);
      clearTenantContext();
    }
  };

  const loadWorkspaces = async () => {
    if (!user) {
      setWorkspaces([]);
      setCurrentWorkspaceState(null);
      setCurrentUserRole(null);
      setIsWorkspaceOwner(false);
      setLoading(false);
      isInitialLoad.current = true;
      previousUserId.current = undefined;
      return;
    }

    // Check if this is just a token refresh (same user ID) and we already have a workspace
    // If so, skip the workspace reload to prevent auto-switching
    const hasUserIdChanged = previousUserId.current !== user.id;
    if (!isInitialLoad.current && !hasUserIdChanged && currentWorkspace) {
      console.log('[WorkspaceContext] Skipping workspace reload - token refresh detected, keeping current workspace');
      setLoading(false);
      return;
    }

    // Update the previous user ID
    previousUserId.current = user.id;

    try {
      // Get user's profile with current workspace
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('current_workspace_id, workspace_settings')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return;
      }

      // Check if user is a super admin via ops_team_members table
      let isSuperAdmin = false;
      try {
        const { data: opsTeamMember } = await supabase
          .from('ops_team_members')
          .select('role')
          .eq('email', user.email?.toLowerCase())
          .eq('is_active', true)
          .eq('role', 'super_admin')
          .maybeSingle();
        isSuperAdmin = !!opsTeamMember;
      } catch (e) {
        console.log('[WorkspaceContext] ops_team_members check failed:', e);
      }

      let allWorkspaces: any[] = [];

      if (isSuperAdmin) {
        // Super admin: fetch ALL workspaces
        console.log('[WorkspaceContext] Super admin detected, loading all workspaces');
        const { data: allWsData, error: allWsError } = await supabase
          .from('workspaces')
          .select('*')
          .order('is_default', { ascending: false })
          .order('created_at');

        if (allWsError) {
          console.error('Error fetching all workspaces for super admin:', allWsError);
          return;
        }

        if (allWsData && allWsData.length > 0) {
          // Fetch owner emails for all workspaces
          const ownerIds = [...new Set(allWsData.map((ws: any) => ws.owner_id))];
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, email')
            .in('id', ownerIds);

          const emailMap = new Map(profilesData?.map(p => [p.id, p.email]) || []);
          allWorkspaces = allWsData.map((ws: any) => ({
            ...ws,
            owner_email: ws.owner_id === user.id ? user.email : (emailMap.get(ws.owner_id) || null),
            is_super_admin_access: ws.owner_id !== user.id // Flag for workspaces accessed via super admin
          }));
          console.log('[WorkspaceContext] Super admin loaded', allWorkspaces.length, 'total workspaces');
        }
      } else {
        // Regular user: fetch owned, team, member, and managed workspaces

      // Fetch only the current user's workspaces
      const result = await supabase
        .from('workspaces')
        .select('*')
        .eq('owner_id', user.id)
        .order('is_default', { ascending: false })
        .order('created_at');

      const ownedWorkspaces = result.data?.map((ws: any) => ({
        ...ws,
        owner_email: user.email // User can see their own email
      }));
      const ownedError = result.error;

      // Try to get workspaces from workspace_members, workspace_users, and managed_by (if tables exist)
      let memberWorkspaces: any[] = [];
      let teamWorkspaces: any[] = [];
      let managedWorkspaces: any[] = [];

      // Get workspaces from workspace_users (team invites) - using two-step query to avoid join issues
      try {
        // Step 1: Get workspace IDs where user is an active team member
        const { data: userWorkspaces, error: userWorkspacesError } = await supabase
          .from('workspace_users')
          .select('workspace_id, role_type')
          .eq('user_id', user.id)
          .eq('invitation_status', 'active');

        if (!userWorkspacesError && userWorkspaces && userWorkspaces.length > 0) {
          const workspaceIds = userWorkspaces.map(wu => wu.workspace_id);
          const roleMap = new Map(userWorkspaces.map(wu => [wu.workspace_id, wu.role_type]));

          // Step 2: Fetch workspace details
          const { data: workspacesData, error: workspacesError } = await supabase
            .from('workspaces')
            .select('*')
            .in('id', workspaceIds)
            .order('is_default', { ascending: false })
            .order('created_at');

          if (!workspacesError && workspacesData && workspacesData.length > 0) {
            console.log('[WorkspaceContext] Found team workspaces via workspace_users:', workspacesData.length);
            // Fetch owner emails separately
            const ownerIds = [...new Set(workspacesData.map((ws: any) => ws.owner_id))];
            const { data: profilesData } = await supabase
              .from('profiles')
              .select('id, email')
              .in('id', ownerIds);

            const emailMap = new Map(profilesData?.map(p => [p.id, p.email]) || []);
            teamWorkspaces = workspacesData.map((ws: any) => ({
              ...ws,
              owner_email: emailMap.get(ws.owner_id) || null,
              user_role_type: roleMap.get(ws.id) // Store the user's role
            }));
          }
        }
      } catch (e) {
        // workspace_users query might fail, that's okay
        console.log('[WorkspaceContext] workspace_users query error:', e);
      }

      // Get workspaces from workspace_members
        try {
          const { data: workspacesData, error: workspacesError } = await supabase
            .from('workspaces')
            .select(`
              *,
              workspace_members!inner(user_id, role)
            `)
            .eq('workspace_members.user_id', user.id)
            .order('is_default', { ascending: false })
            .order('created_at');

          if (!workspacesError && workspacesData && workspacesData.length > 0) {
            // Fetch owner emails separately
            const ownerIds = [...new Set(workspacesData.map((ws: any) => ws.owner_id))];
            const { data: profilesData } = await supabase
              .from('profiles')
              .select('id, email')
              .in('id', ownerIds);

            const emailMap = new Map(profilesData?.map(p => [p.id, p.email]) || []);
            memberWorkspaces = workspacesData.map((ws: any) => ({
              ...ws,
              owner_email: emailMap.get(ws.owner_id) || null
            }));
          }
        } catch (e) {
          // workspace_members table might not exist, that's okay
          console.log('workspace_members table not available');
        }

        // Get workspaces managed by operations team (where user is in managed_by array)
        try {
          const { data: managedData, error: managedError } = await supabase
            .from('workspaces')
            .select('*')
            .contains('managed_by', [user.id])
            .order('is_default', { ascending: false })
            .order('created_at');

          if (!managedError && managedData && managedData.length > 0) {
            console.log('[WorkspaceContext] Found managed workspaces:', managedData.length);
            // Fetch owner emails separately
            const ownerIds = [...new Set(managedData.map((ws: any) => ws.owner_id))];
            const { data: profilesData } = await supabase
              .from('profiles')
              .select('id, email')
              .in('id', ownerIds);

            const emailMap = new Map(profilesData?.map(p => [p.id, p.email]) || []);
            managedWorkspaces = managedData.map((ws: any) => ({
              ...ws,
              owner_email: emailMap.get(ws.owner_id) || null,
              is_managed: true // Flag to indicate this is a managed workspace
            }));
          }
        } catch (e) {
          // managed_by column might not exist yet, that's okay
          console.log('managed_by field not available yet');
        }

      // Combine and deduplicate workspaces
      allWorkspaces = [...(ownedWorkspaces || [])];
      if (teamWorkspaces.length > 0) {
        teamWorkspaces.forEach(ws => {
          if (!allWorkspaces.find(ow => ow.id === ws.id)) {
            allWorkspaces.push(ws);
          }
        });
      }
      if (memberWorkspaces.length > 0) {
        memberWorkspaces.forEach(ws => {
          if (!allWorkspaces.find(ow => ow.id === ws.id)) {
            allWorkspaces.push(ws);
          }
        });
      }
      if (managedWorkspaces.length > 0) {
        managedWorkspaces.forEach(ws => {
          if (!allWorkspaces.find(ow => ow.id === ws.id)) {
            allWorkspaces.push(ws);
          }
        });
      }

      if (ownedError) {
        console.error('Error fetching owned workspaces:', ownedError);
        return;
      }

      } // end of regular user block

      setWorkspaces(allWorkspaces || []);

      // Check for workspace query parameter
      const urlParams = new URLSearchParams(window.location.search);
      const workspaceParam = urlParams.get('workspace');

      // Set current workspace
      let workspaceToSet: Workspace | null = null;

      if (workspaceParam && allWorkspaces) {
        // If workspace parameter is provided, check if user has access
        const requestedWorkspace = allWorkspaces.find(w => w.id === workspaceParam);
        if (requestedWorkspace) {
          console.log('[WorkspaceContext] Setting workspace from URL parameter:', requestedWorkspace.name);
          workspaceToSet = requestedWorkspace;
        } else {
          console.warn('[WorkspaceContext] User does not have access to workspace:', workspaceParam);
        }
      }

      // Check localStorage for this browser's workspace preference (per-browser, not shared via DB)
      // This prevents workspace switching by one person from affecting other people sharing the same account
      const localWorkspaceKey = `workspace_pref_${user.id}`;
      const localWorkspaceId = localStorage.getItem(localWorkspaceKey);
      if (!workspaceToSet && localWorkspaceId && allWorkspaces) {
        const localWorkspace = allWorkspaces.find(w => w.id === localWorkspaceId);
        if (localWorkspace) {
          console.log('[WorkspaceContext] Setting workspace from localStorage:', localWorkspace.name);
          workspaceToSet = localWorkspace;
        }
      }

      if (!workspaceToSet && profile.current_workspace_id && allWorkspaces) {
        const current = allWorkspaces.find(w => w.id === profile.current_workspace_id);
        workspaceToSet = current || allWorkspaces[0] || null;
      } else if (!workspaceToSet && allWorkspaces && allWorkspaces.length > 0) {
        // If no current workspace set, use the default or first one
        workspaceToSet = allWorkspaces.find(w => w.is_default) || allWorkspaces[0];
      }

      if (workspaceToSet) {
        setCurrentWorkspaceState(workspaceToSet);
        // Load the user's role for this workspace
        loadUserRole(workspaceToSet.id, user.id, (workspaceToSet as any).owner_id);
        // Mirror the setCurrentWorkspace() wrapper's Sentry tagging.
        // This branch bypasses the wrapper (calls setCurrentWorkspaceState
        // directly) so we tag inline here — every Sentry event on this
        // session carries workspace_id + agency_id from now on.
        setTenantContext({
          workspace_id: workspaceToSet.id,
          agency_id: (workspaceToSet as any).agency_id ?? null,
          is_agency_domain:
            typeof window !== 'undefined' &&
            !!(window as any).__AGENCY_BRANDING__?.id,
          agency_slug:
            typeof window !== 'undefined'
              ? (window as any).__AGENCY_BRANDING__?.slug ?? null
              : null,
        });

        // Persist this browser's workspace preference locally (avoids shared-account conflicts)
        localStorage.setItem(`workspace_pref_${user.id}`, workspaceToSet.id);

        // Auto-fix: If on agency domain and workspace doesn't have agency_id, set it
        const agencyId = typeof window !== 'undefined' && (window as any).__AGENCY_BRANDING__?.id;
        if (agencyId && !(workspaceToSet as any).agency_id) {
          console.log('[WorkspaceContext] Auto-fixing workspace agency_id:', agencyId);
          await supabase
            .from('workspaces')
            .update({ agency_id: agencyId })
            .eq('id', workspaceToSet.id);
          // Update local state
          (workspaceToSet as any).agency_id = agencyId;
        }

        // Update profile with current workspace (best-effort sync — localStorage takes priority on load)
        await supabase
          .from('profiles')
          .update({ current_workspace_id: workspaceToSet.id })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    } finally {
      setLoading(false);
      isInitialLoad.current = false;
    }
  };

  // Use user?.id instead of user to prevent re-running on token refresh
  // The user object changes when tokens refresh, but user.id remains stable
  useEffect(() => {
    loadWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const switchWorkspace = async (workspaceId: string) => {
    if (!user) return;

    try {
      // Find the workspace to ensure user has access
      let workspace = workspaces.find(w => w.id === workspaceId);

      // If workspace not found in local state, try to fetch it from database
      if (!workspace) {
        const { data: fetchedWorkspace, error: fetchError } = await supabase
          .from('workspaces')
          .select('*')
          .eq('id', workspaceId)
          .single();

        if (fetchError || !fetchedWorkspace) {
          throw new Error('Workspace not found or you do not have access to it');
        }

        // Add to local state if fetched successfully
        workspace = fetchedWorkspace;
        setWorkspaces(prev => {
          const exists = prev.some(w => w.id === workspace!.id);
          if (exists) {
            return prev;
          }
          return [...prev, workspace!];
        });
      }

      // Save this browser's workspace preference locally (per-browser, prevents shared-account conflicts)
      localStorage.setItem(`workspace_pref_${user.id}`, workspaceId);

      // Update user's current workspace directly in profiles table
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ current_workspace_id: workspaceId })
        .eq('id', user.id);

      if (updateError) {
        // Try using RPC function as fallback
        const { error } = await supabase.rpc('switch_workspace', {
          p_workspace_id: workspaceId
        });

        if (error) {
          throw error;
        }
      }

      // Update local state
      setCurrentWorkspace(workspace);
      toast({
        title: "Workspace switched",
        description: `Now working in ${workspace.name}`
      });

      // Force all workspace-dependent components to remount with fresh state
      // Using a key-based remount instead of full page reload to avoid auth flash
      setWorkspaceResetKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Error switching workspace:', error);
      toast({
        title: "Failed to switch workspace",
        description: error.message || "An error occurred while switching workspace",
        variant: "destructive"
      });
    }
  };

  const createWorkspace = async (data: {
    name: string;
    brand_name?: string;
    logo_url?: string;
    website?: string;
    notification_email?: string;
    notification_phone?: string;
    business_description?: string;
    country?: string;
    timezone?: string;
    agency_id?: string;
  }): Promise<Workspace | null> => {
    if (!user) {
      console.error('No user found when creating workspace');
      return null;
    }

    try {
      console.log('Creating workspace with data:', data);

      // Try using the RPC function first
      const { data: rpcResult, error: rpcError } = await supabase
        .rpc('create_workspace_with_member', {
          p_name: data.name,
          p_brand_name: data.brand_name || null,
          p_logo_url: data.logo_url || null,
          p_website: data.website || null,
          p_notification_email: data.notification_email || null,
          p_notification_phone: data.notification_phone || null,
          p_business_description: data.business_description || null,
          p_country: data.country || null,
          p_timezone: data.timezone || null
        });

      let workspaceId: string | null = null;

      if (rpcError) {
        console.log('RPC function not available or failed, using direct insert:', rpcError);

        // Fallback to direct insert
        const { data: newWorkspace, error: createError } = await supabase
          .from('workspaces')
          .insert({
            owner_id: user.id,
            name: data.name,
            brand_name: data.brand_name,
            logo_url: data.logo_url,
            website: data.website,
            notification_email: data.notification_email,
            notification_phone: data.notification_phone,
            business_description: data.business_description,
            country: data.country,
            timezone: data.timezone,
            agency_id: data.agency_id || null,
            is_default: false
          })
          .select()
          .single();

        if (createError) {
          console.error('Direct insert error:', createError);
          throw createError;
        }

        workspaceId = newWorkspace.id;

        // Add user as owner in workspace_members
        const { error: memberError } = await supabase
          .from('workspace_members')
          .insert({
            workspace_id: newWorkspace.id,
            user_id: user.id,
            role: 'owner'
          });

        if (memberError) {
          console.error('Error adding workspace member:', memberError);
          // Don't throw here, workspace was created successfully
        }
      } else {
        if (!rpcResult || rpcResult.length === 0) {
          throw new Error('Workspace creation returned no data');
        }
        workspaceId = rpcResult[0].workspace_id;
        console.log('Workspace created via RPC:', workspaceId);
      }

      // Fetch the newly created workspace directly
      const { data: newWorkspace, error: fetchError } = await supabase
        .from('workspaces')
        .select('*')
        .eq('id', workspaceId)
        .single();

      if (fetchError) {
        console.error('Error fetching new workspace:', fetchError);
        throw new Error('Failed to fetch the newly created workspace');
      }

      // Add the new workspace to the local state immediately
      // Check if workspace already exists to prevent duplicates
      setWorkspaces(prev => {
        const exists = prev.some(w => w.id === newWorkspace.id);
        if (exists) {
          return prev;
        }
        return [...prev, newWorkspace];
      });

      toast({
        title: "Workspace created",
        description: `${data.name} has been created successfully`
      });

      return newWorkspace;
    } catch (error: any) {
      console.error('Error creating workspace:', error);
      
      // Provide more specific error messages
      let errorMessage = "An error occurred while creating the workspace";
      
      if (error.code === '42501') {
        errorMessage = "Permission denied. Please ensure you're logged in.";
      } else if (error.code === '23505') {
        errorMessage = "A workspace with this name already exists.";
      } else if (error.code === '23503') {
        errorMessage = "Invalid user reference. Please refresh and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Failed to create workspace",
        description: errorMessage,
        variant: "destructive"
      });
      return null;
    }
  };

  const updateWorkspace = async (workspaceId: string, data: Partial<Workspace>) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('workspaces')
        .update(data)
        .eq('id', workspaceId)
        .eq('owner_id', user.id);

      if (error) {
        throw error;
      }

      // Refresh workspaces list
      await loadWorkspaces();

      toast({
        title: "Workspace updated",
        description: "Workspace settings have been saved"
      });
    } catch (error: any) {
      console.error('Error updating workspace:', error);
      toast({
        title: "Failed to update workspace",
        description: error.message || "An error occurred while updating the workspace",
        variant: "destructive"
      });
    }
  };

  const deleteWorkspace = async (workspaceId: string) => {
    if (!user) return;

    try {
      // Check if it's the default workspace
      const workspace = workspaces.find(w => w.id === workspaceId);
      if (workspace?.is_default) {
        toast({
          title: "Cannot delete default workspace",
          description: "The default workspace cannot be deleted",
          variant: "destructive"
        });
        return;
      }

      // Delete the entire workspace folder from brand-logos storage
      // Storage structure: {userId}/{workspaceId}/logo_{timestamp}.{ext}
      try {
        const folderPath = `${user.id}/${workspaceId}`;
        console.log('Deleting workspace brand logos folder:', folderPath);

        // List all files in the workspace folder
        const { data: files, error: listError } = await supabase.storage
          .from('brand-logos')
          .list(folderPath);

        if (listError) {
          console.error('Error listing brand logos:', listError);
        } else if (files && files.length > 0) {
          // Delete all files in the workspace folder
          const filePaths = files.map(file => `${folderPath}/${file.name}`);
          console.log('Deleting brand logo files:', filePaths);

          const { error: deleteError } = await supabase.storage
            .from('brand-logos')
            .remove(filePaths);

          if (deleteError) {
            console.error('Error deleting brand logos:', deleteError);
            // Continue with workspace deletion even if logo deletion fails
          } else {
            console.log('Successfully deleted brand logos for workspace');
          }
        }
      } catch (logoError) {
        console.error('Error processing logo deletion:', logoError);
        // Continue with workspace deletion even if logo deletion fails
      }

      // Delete all product images for this workspace
      // Storage structure: {userId}/{workspaceId}/{timestamp}_{random}.{ext}
      try {
        const productImagesFolderPath = `${user.id}/${workspaceId}`;
        console.log('Deleting workspace product images folder:', productImagesFolderPath);

        // List all product images in the workspace folder
        const { data: productFiles, error: listProductError } = await supabase.storage
          .from('product-images')
          .list(productImagesFolderPath);

        if (listProductError) {
          console.error('Error listing product images:', listProductError);
        } else if (productFiles && productFiles.length > 0) {
          // Delete all product images in the workspace folder
          const productFilePaths = productFiles.map(file => `${productImagesFolderPath}/${file.name}`);
          console.log('Deleting product image files:', productFilePaths);

          const { error: deleteProductError } = await supabase.storage
            .from('product-images')
            .remove(productFilePaths);

          if (deleteProductError) {
            console.error('Error deleting product images:', deleteProductError);
            // Continue with workspace deletion even if product images deletion fails
          } else {
            console.log('Successfully deleted product images for workspace');
          }
        }
      } catch (productError) {
        console.error('Error processing product images deletion:', productError);
        // Continue with workspace deletion even if product images deletion fails
      }

      const { error } = await supabase
        .from('workspaces')
        .delete()
        .eq('id', workspaceId)
        .eq('owner_id', user.id);

      if (error) {
        throw error;
      }

      // If the deleted workspace was the current one, switch to default
      if (currentWorkspace?.id === workspaceId) {
        const defaultWorkspace = workspaces.find(w => w.is_default);
        if (defaultWorkspace) {
          await switchWorkspace(defaultWorkspace.id);
        }
      }

      // Refresh workspaces list
      await loadWorkspaces();

      toast({
        title: "Workspace deleted",
        description: "The workspace has been deleted successfully"
      });
    } catch (error: any) {
      console.error('Error deleting workspace:', error);
      toast({
        title: "Failed to delete workspace",
        description: error.message || "An error occurred while deleting the workspace",
        variant: "destructive"
      });
    }
  };

  const refreshWorkspaces = async () => {
    await loadWorkspaces();
  };

  const searchWorkspaces = async (query: string, offset: number = 0, limit: number = 25): Promise<WorkspaceSearchResult> => {
    if (!user) {
      return { workspaces: [], hasMore: false, total: 0 };
    }

    try {
      // Build the search pattern - search in name and brand_name only
      const searchPattern = query.trim() ? `%${query.trim()}%` : '';

      // Get ALL owned workspaces matching the search (no database pagination - we paginate after combining)
      let ownedQuery = supabase
        .from('workspaces')
        .select('*')
        .eq('owner_id', user.id);

      if (searchPattern) {
        ownedQuery = ownedQuery.or(`name.ilike.${searchPattern},brand_name.ilike.${searchPattern}`);
      }

      const { data: ownedData, error: ownedError } = await ownedQuery
        .order('is_default', { ascending: false })
        .order('name');

      if (ownedError) {
        console.error('Error searching owned workspaces:', ownedError);
        throw ownedError;
      }

      const ownedWorkspaces = ownedData?.map((ws: any) => ({
        ...ws,
        owner_email: user.email
      })) || [];

      // Get ALL workspaces from workspace_members matching the search
      let memberWorkspaces: any[] = [];
      try {
        let memberQuery = supabase
          .from('workspaces')
          .select(`*, workspace_members!inner(user_id, role)`)
          .eq('workspace_members.user_id', user.id)
          .neq('owner_id', user.id); // Exclude owned workspaces

        if (searchPattern) {
          memberQuery = memberQuery.or(`name.ilike.${searchPattern},brand_name.ilike.${searchPattern}`);
        }

        const { data: memberData, error: memberError } = await memberQuery.order('name');

        if (!memberError && memberData && memberData.length > 0) {
          // Fetch owner emails separately
          const ownerIds = [...new Set(memberData.map((ws: any) => ws.owner_id))];
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, email')
            .in('id', ownerIds);
          const emailMap = new Map(profilesData?.map(p => [p.id, p.email]) || []);
          memberWorkspaces = memberData.map((ws: any) => ({
            ...ws,
            owner_email: emailMap.get(ws.owner_id) || null
          }));
        }
      } catch (e) {
        console.log('workspace_members search not available');
      }

      // Get ALL managed workspaces matching the search
      let managedWorkspaces: any[] = [];
      try {
        let managedQuery = supabase
          .from('workspaces')
          .select('*')
          .contains('managed_by', [user.id])
          .neq('owner_id', user.id); // Exclude owned workspaces

        if (searchPattern) {
          managedQuery = managedQuery.or(`name.ilike.${searchPattern},brand_name.ilike.${searchPattern}`);
        }

        const { data: managedData, error: managedError } = await managedQuery.order('name');

        if (!managedError && managedData && managedData.length > 0) {
          // Fetch owner emails separately
          const ownerIds = [...new Set(managedData.map((ws: any) => ws.owner_id))];
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, email')
            .in('id', ownerIds);
          const emailMap = new Map(profilesData?.map(p => [p.id, p.email]) || []);
          managedWorkspaces = managedData.map((ws: any) => ({
            ...ws,
            owner_email: emailMap.get(ws.owner_id) || null,
            is_managed: true
          }));
        }
      } catch (e) {
        console.log('managed_by search not available');
      }

      // Combine and deduplicate ALL results first
      const allWorkspaces: any[] = [...ownedWorkspaces];
      const seenIds = new Set(ownedWorkspaces.map(w => w.id));

      memberWorkspaces.forEach(ws => {
        if (!seenIds.has(ws.id)) {
          allWorkspaces.push(ws);
          seenIds.add(ws.id);
        }
      });

      managedWorkspaces.forEach(ws => {
        if (!seenIds.has(ws.id)) {
          allWorkspaces.push(ws);
          seenIds.add(ws.id);
        }
      });

      // Now apply pagination on combined results
      const totalCount = allWorkspaces.length;
      const paginatedWorkspaces = allWorkspaces.slice(offset, offset + limit);
      const hasMore = offset + paginatedWorkspaces.length < totalCount;

      return {
        workspaces: paginatedWorkspaces,
        hasMore,
        total: totalCount
      };
    } catch (error) {
      console.error('Error searching workspaces:', error);
      return { workspaces: [], hasMore: false, total: 0 };
    }
  };

  const value: WorkspaceContextType = {
    currentWorkspace,
    workspaces,
    loading,
    currentUserRole,
    isWorkspaceOwner,
    switchWorkspace,
    setCurrentWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    refreshWorkspaces,
    searchWorkspaces,
    workspaceResetKey
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};